'use client';

import { WhatsappLogo, X } from '@phosphor-icons/react';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useStoreContact } from '@/lib/storefront/store-contact';
import {
  buildWhatsAppUrl,
  supportMessage,
} from '@/lib/storefront/whatsapp-link';
import { Z } from '@/lib/z-index';

/**
 * Floating "need help?" bubble pinned to the bottom corner, opening a small
 * panel that hands the customer off to the store's WhatsApp.
 *
 * Sits above the drawer layer but below modals, so a dialog always covers it.
 * Hidden entirely when the store has no usable WhatsApp number.
 */
export function WhatsAppHelpBubble() {
  const { whatsappPhone, storeName } = useStoreContact();
  const locale = useLocale();
  const isRTL = locale === 'ar';
  // Product pages already carry a "have a question" prompt; display-font
  // stores hide the bubble there (see globals.css).
  const onProductPage = /\/products\/[^/]+/.test(usePathname() ?? '');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Click-away and Escape, so the panel never strands itself open over the
  // content the shopper is trying to read.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!whatsappPhone) return null;

  const title = isRTL ? 'هل من استفسار؟' : 'Need help?';
  const body = isRTL
    ? 'فريقنا متاح على واتساب ويردّ في أقرب وقت.'
    : 'Message us on WhatsApp and we will get back to you shortly.';
  const cta = isRTL ? 'بدء المحادثة' : 'Start chat';

  const href = buildWhatsAppUrl(
    whatsappPhone,
    supportMessage({ storeName, isRTL }),
  );

  return (
    // `right`, not `end`: the bubble belongs in the bottom-right corner in
    // both directions. The logical property put it bottom-left on the Arabic
    // storefront, where it collided with the push-notification prompt.
    // On small screens it sits 12px above the mobile bottom bar, anchored to
    // --bottom-bar-height so it never overlaps the bar; bottom-6 from md up
    // where that bar is hidden. The container is exactly the button's
    // width so the panel can hang off it with `right-0` and grow leftward
    // without any direction-dependent flex alignment.
    <div
      ref={rootRef}
      data-whatsapp-bubble=""
      data-wa-offset=""
      data-pdp={onProductPage ? '' : undefined}
      className={`fixed bottom-24 right-4 md:bottom-6 md:right-6 w-14 ${Z.DRAWER} print:hidden`}
    >
      {open && (
        <div
          role="dialog"
          aria-label={title}
          className="absolute bottom-full right-0 mb-3 w-[min(18rem,calc(100vw-2rem))] rounded-2xl border border-border bg-background p-4 shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-200 motion-reduce:animate-none"
        >
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={isRTL ? 'إغلاق' : 'Close'}
              className="tap-target -m-1 p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            {body}
          </p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-[var(--radius-field)] bg-[#25D366] text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            <WhatsappLogo size={18} weight="fill" />
            {cta}
          </a>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={title}
        className="h-14 w-14 rounded-full bg-[#25D366] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105 active:scale-95 motion-reduce:transition-none"
      >
        <WhatsappLogo size={28} weight="fill" />
      </button>
    </div>
  );
}
